import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { ActionResponse } from "@/javascript/conduction-api";
import { Utils } from "@/javascript/utils";
import {
	Anchor,
	AspectRatio,
	Badge,
	Button,
	Divider,
	Group,
	PasswordInput,
	Stack,
	Text,
	TextInput,
	Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconMicrophone2, IconUser } from "@tabler/icons-react";
import React, { useContext, useState } from "react";

interface SendToMusiciansFormProps {
	onClose: () => void;
}

const SendToMusiciansForm: React.FC<SendToMusiciansFormProps> = ({ onClose }) => {
	const [submitting, setSubmitting] = useState(false);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("SendToMusiciansForm must be a child of AuthContext.");
	}

	const token = auth.token;
	const user = auth.user;
	const project = auth.project;

	if (!token || !user) return null;

	// ---- role & context flags -------------------------------------------------
	const userIsClassroomAdmin = user.roles.includes("ClassroomAdmin");
	const userIsConductionAdmin = user.roles.includes("ConductionAdmin");
	const userIsGuest = user.roles.includes("Guest");

	const userAppearsInClassroomByRole =
		user.roles.includes("ClassroomAdmin") || user.roles.includes("Student");
	const inClassroom = Boolean(auth.classroom) || userAppearsInClassroomByRole;

	const needsTeacherCreds =
		inClassroom && !userIsClassroomAdmin && !userIsConductionAdmin;

	const form = useForm({
		initialValues: { username: "", password: "" },
		validate: {
			username: (v) =>
				needsTeacherCreds && v.trim().length === 0
					? "Teacher username is required"
					: null,
			password: (v) =>
				needsTeacherCreds && v.length === 0
					? "Teacher password is required"
					: null,
		},
	});

	const remainingSubmissionCredits = inClassroom
		? auth.classroom?.getRemainingActiveCredits()
		: user.roles.includes("Guest") ? 5 : user.getRemainingActiveCredits();

	const hasPendingSubmission =
		project?.submissions?.some((s) => s.submissionStatus !== "Uploaded") ?? false;

	const outOfCredits =
		!userIsConductionAdmin &&
		(remainingSubmissionCredits === undefined || remainingSubmissionCredits <= 0);

	const isTourLocked = auth.beginnerTourActive;

	// ---- disable logic & reason tooltip --------------------------------------
	const submitDisabled =
		isTourLocked || outOfCredits || hasPendingSubmission || submitting;

	const disableReason = (() => {
		if (submitting) return "Submission already in progress.";
		if (isTourLocked) return "Disabled during the beginner tour.";
		if (hasPendingSubmission)
			return "A submission for this project is already being recorded.";
		if (outOfCredits) return "No remaining submission credits.";
		return "";
	})();

	// ---- handlers -------------------------------------------------------------
	const handleSubmit = async () => {
		if (!project || userIsGuest) {
			Utils.displayNotification(
				"You must be logged in order to send a project to musicians.",
				undefined,
				"blue",
				<IconAlertCircle size={16} />
			);
			return;
		}

		if (needsTeacherCreds) {
			const result = form.validate();
			if (result.hasErrors) return;
		}

		if (project.isDirty() && !project.isLocked() && project.userCanEdit(user)) {
			await auth.saveProject();
		}

		const projectId = project.getId();
		setSubmitting(true);

		const notificationId = Utils.displayNotification(
			"Attempting submission...",
			"This could take several minutes to process.",
			"blue",
			"loading"
		);

		try {
			const instructorUsername = form.values.username;
			const instructorPassword = form.values.password;

			const response: ActionResponse = await ConductionAPI.sendToMusicians(
				projectId,
				instructorUsername,
				instructorPassword,
				inClassroom,
				token
			);

			if (response.success) {
				Utils.updateNotification(
					notificationId,
					"Success!",
					"This project has been sent to musicians for recording!",
					"green",
					<IconMicrophone2 size={16} />
				);

				auth.lockProject();
				onClose();
			} else {
				Utils.updateNotification(
					notificationId,
					response.message,
					response.errorDetails,
					"red",
					<IconAlertCircle size={16} />,
					true,
					9000
				);
			}
		} catch {
			Utils.updateNotification(
				notificationId,
				"Submission Error",
				"Something went wrong while submitting this project. Please try again later.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		} finally {
			setSubmitting(false);
		}
	};

	// ---- UI -------------------------------------------------------------------
	return (
		<ConductionModal
			onClose={onClose}
			title="Send to Musicians"
			size="lg"
		>
			<Stack align="center" justify="center">
				<Text size="lg">Ready to see your creation come to life with real musicians?</Text>
				<Text size="md">Here's an example of what it could look like:</Text>

				<AspectRatio ratio={16 / 9} maw={600} mx="auto" style={{ width: "80%" }}>
					<iframe
						src="https://www.youtube.com/embed/ldClt8z4lU8?si=iMnSvZnk5sHNKCax"
						title="YouTube video player"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerPolicy="strict-origin-when-cross-origin"
						allowFullScreen
					/>
				</AspectRatio>

				{user.roles.includes("Guest") ? (
					<Button size="lg" onClick={() => { onClose(); auth.logout(); }} disabled={submitDisabled} loading={submitting} leftSection={<IconUser size={16} />}>
						Click here to take the next step and create an account!
					</Button>
				) : (
					<>
						{needsTeacherCreds && (
							<Text ta="center">
								Your instructor must provide their login credentials to complete the submission.
							</Text>
						)}

						<Text size="sm" c="red" ta="center">
							Projects will be recorded over the following week. You will be notified via email when your
							recording is ready to view and download!
						</Text>

						<form onSubmit={form.onSubmit(handleSubmit)} style={{ width: "100%" }}>
							<Stack align="center">
								{needsTeacherCreds && (
									<Stack w="min(560px, 90%)">
										<TextInput
											label="Teacher Username"
											{...form.getInputProps("username")}
											required
											disabled={submitting}
										/>
										<PasswordInput
											label="Teacher Password"
											{...form.getInputProps("password")}
											required
											disabled={submitting}
										/>
									</Stack>
								)}


								<Group wrap="wrap" justify="center">
									<Tooltip
										label={disableReason || "Submit your project to musicians!"}
										withArrow
										color={disableReason ? "red" : "gray"}
										position="top"
										disabled={!submitDisabled}
										openDelay={200}
									>
										<Button onClick={handleSubmit} disabled={submitDisabled} loading={submitting}>
											{hasPendingSubmission ? (
												<Text>This project is being recorded!</Text>
											) : (
												<Text>Send my project to musicians!</Text>
											)}
										</Button>
									</Tooltip>

									{remainingSubmissionCredits !== undefined && (
										<Badge
											color={
												remainingSubmissionCredits > 3
													? "blue"
													: remainingSubmissionCredits > 0
														? "yellow"
														: "red"
											}
										>
											{remainingSubmissionCredits} Credits Remaining
										</Badge>
									)}

									{!inClassroom && (
										<Stack w="100%" align="center">
											<Divider w="100%" />
											<Stack gap={2} align="center">
												<Button
													size="lg"
													component="a"
													onClick={() => window.open("https://buy.stripe.com/cNi3cv2Vo06AcFJbws8N203", '_blank')}
												>
													<Text>
														Need more submission credits? Click here to get more!
													</Text>
												</Button>
												<Text size="xs" c="gray">
													Credits will be added to your account within 24 hours of purchase.
												</Text>
												<Text size="xs" c="gray">
													Email us at <Anchor onClick={() => window.open("mailto:contact@conduction.live")} c="black">contact@conduction.live</Anchor> for more information.
												</Text>
											</Stack>
										</Stack>
									)}
								</Group>
							</Stack>
						</form>
					</>
				)}
			</Stack>
		</ConductionModal>
	);
};

export default SendToMusiciansForm;
